import React, { useEffect, useMemo, useRef, useState } from "react";
import { UserView, UserViewType } from "../../types/user";
import { useUserRegistry } from "../../hooks/useUserRegistry";
import { HbrInput, HbrTabGroup } from "@harbor/elements-react-standalone";
import UserViewNavigationItem from "./UserViewNavigationItem";
import StbButton from "../security-toolbox-ui/StbButton";
import { plus, copy } from '@harbor/elements/icons';
import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import { Column, getColumnTypeConfig, getColumnTypeForPermission } from "../../types/table";
import { defaultColumns, fixedColumns, permissionColumnMap } from "../../support/config/tables";
import { checkSalesforcePermissionsLoaded, selectSalesforcePermissionById } from "../../support/slices/salesforcePermissionsSlice";
import { RootState, useAppDispatch } from "../../support/store";
import { useSelector } from "react-redux";
import StbModal from "../security-toolbox-ui/StbModal";
import { titleCase } from "../security-toolbox-ui/security-toolbox-ui-utils";



interface UserViewNavigationProps {
  viewType: UserViewType;
  selectedView?: UserView | null;
  setSelectedView: (view: UserView) => void;
}

const UserViewNavigation: React.FC<UserViewNavigationProps> = ({
  viewType,
  selectedView,
  setSelectedView
}) => {
  const dispatch = useAppDispatch();

  // View Registry Hooks
  const {
    userViews,
    addUserView,
    deleteUserView,
  } = useUserRegistry();

  const [views, setViews] = useState<UserView[]>([]);

  // Navigation Tab Hooks
  const tabGroupRef = useRef<HTMLHbrTabGroupElement | null>(null);

  useEffect(() => {
    if (!selectedView && views.length > 0) {
      setSelectedView(views[0]);
    }
  }, [views, selectedView, setSelectedView]);

  useEffect(() => {
    const views = userViews[viewType];
    setViews(views ?? []);
    setSelectedView(views?.find(v => v.id === selectedView?.id) ?? views?.[0] ?? null);
  }, [userViews, viewType, selectedView, setSelectedView]);

  // Hack the styling that Harbor Tab Group applies inside shadow DOM - only works while shadow root is open so will need to be checked if Harbor updated
  useEffect(() => {
    const tabGroup = tabGroupRef?.current;
    if (!tabGroup || !tabGroup.shadowRoot) return;

    const observer = new MutationObserver(() => {
      const leftIconButton: HTMLElement = tabGroup.shadowRoot.querySelector('.tab-group__scroll-button--left');
      if (leftIconButton) {
        leftIconButton.style.justifyContent = 'flex-start';
        const leftButton = leftIconButton.shadowRoot?.querySelector('button');
        if (leftButton) {
          leftButton.style.paddingLeft = '0';
        }
        observer.disconnect();
      }
    });

    observer.observe(tabGroup.shadowRoot, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [tabGroupRef, views]);

  // Add Modal Hooks
  const [showAddModal, setShowAddModal] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [columnInputFieldValue, setColumnInputFieldValue] = useState("");
  const [selectedColumn, setSelectedColumn] = useState<Column>(null);
  const [selectedColumns, setSelectedColumns] = useState<Column[]>([]);

  const permissions = useSelector((state: RootState) => selectSalesforcePermissionById(state, permissionColumnMap[viewType]));
  useEffect(() => {
    dispatch(checkSalesforcePermissionsLoaded());
  }, [dispatch]);

  const availableColumns = useMemo(() => {
    return permissions.map(permission => ({
      id: permission.value,
      name: permission.name,
      columnType: getColumnTypeForPermission(permission),
      sortable: !!(permission?.source === 'salesforce')
    }) as Column)
      .filter(col => !selectedColumns.map((col) => col.name).includes(col.name));
  }, [permissions, selectedColumns]);

  // Handlers
  const handleAddView = async () => {
    const newView: UserView = {
      id: Date.now(),
      name: newViewName,
      type: viewType,
      columns: selectedColumns.length > 0
        ? selectedColumns.map(col => getColumnTypeConfig(col))
        : defaultColumns[viewType],
      filters: [],
    };
    setSelectedView(newView);
    addUserView(viewType, newView);

    setShowAddModal(false);
    setNewViewName('');
    setSelectedColumns([]);
  };

  const handleDeleteView = (viewId: UserView['id']) => {
    if (views?.length === 1) {
      alert('You must have at least one view');
      return;
    }
    deleteUserView(viewType, viewId);
    setSelectedView(views[0] || null);
  };

  const handleAddViewClick = () => {
    setNewViewName(`New View ${views.length + 1}`)
    setShowAddModal(true);
  }

  const handleCloneViewClick = async () => {
    const newView = { ...selectedView, id: Date.now(), name: `${selectedView.name} (Copy)` };
    await addUserView(viewType, newView);
    setSelectedView(newView);
  };
  
  const handleColumnSelectionChange = (column: Column) => {
    if (!column || selectedColumns.map((col) => col.name).includes(column.name)) return;

    const selected: Column = availableColumns.find(col => col.name === column.name);
    if (!selected) return;
  
    setSelectedColumns(prev => [...prev, getColumnTypeConfig(selected)]);
    setSelectedColumn(null);
    setColumnInputFieldValue("");
  }
  
  const handleInputFieldChange = (label: string) => {
    setColumnInputFieldValue(label)
  }

  const handleChipOnDelete = (column: {id: string, name: string}) => {
    setSelectedColumns(prev => prev.filter(col => col.id !== column.id));
  };

  return (
    <>
      <div className="grid grid-cols-[1fr_18rem] items-center px-6">
        <div className="flex justify-start">
          <HbrTabGroup ref={tabGroupRef} className="view-navigation" placement="top">
            {views.map((view: UserView) => (
              <UserViewNavigationItem
                key={view.id}
                view={view}
                viewType={viewType}
                selectedView={selectedView}
                setSelectedView={setSelectedView}
                handleDeleteView={handleDeleteView}
              />
            ))}
          </HbrTabGroup>
        </div>

        {views.length > 0 && (
          <div className="flex ml-auto">
            <StbButton
              role="default"
              icon={plus}
              onClick={handleAddViewClick}
              className="hbr-button-group__button hbr-button-group__button--first"
            >
              Add View
            </StbButton>
            <StbButton
              role="default"
              icon={copy}
              onClick={handleCloneViewClick}
              className="hbr-button-group__button hbr-button-group__button--last"
            >
              Clone Current View
            </StbButton>
          </div>
        )}
      </div>

      <StbModal
        title={`Add ${titleCase(viewType)} View`}
        open={showAddModal}
        submitLabel="Add View"
        submitButtonRole="add"
        onSubmit={handleAddView}
        onClose={async () => setShowAddModal(false)}
      >
        <>
          <HbrInput
            label="Name"
            value={newViewName}
            size="medium"
            onHbr-input={e => setNewViewName(e.target.value)}
            placeholder="View Name"
          />
          {availableColumns.length > 0 ? (
            <div className="flex flex-col">
              <Autocomplete
                options={availableColumns}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedColumn}
                onChange={(event, newValue) => {
                  handleColumnSelectionChange(newValue);
                }}
                inputValue={columnInputFieldValue}
                onInputChange={(event, newInputValue) => {
                  handleInputFieldChange(newInputValue);
                }}
      
                renderInput={(params) => <TextField {...params} label="Select Table Column" />}
                fullWidth
                className={`mt-4 ${selectedColumns.length === 0 ? '' : 'mb-2'}`}
              />
              <Box 
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  position: 'relative',
                }}
              >
                {selectedColumns.filter(c => c.id !== 'selected').map((column) => (
                  <Chip
                    label={column.name}
                    key={column.id}
                    onDelete={!fixedColumns[viewType].includes(column.name) ? () => handleChipOnDelete(column) : undefined}
                    color={fixedColumns[viewType].includes(column.name) ? 'primary' : 'default'}
                    sx={{
                      m: 0.5
                    }}
                  />
                ))}
              </Box>
            </div>
          ) : (
            <p>Loading columns...</p>
          )}
        </>
      </StbModal>
    </>
  );
}

export default UserViewNavigation;